import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { WizardProgress } from './WizardProgress';
import { OrgTypeStep } from './OrgTypeStep';
import { UploadStep } from './UploadStep';
import { IdValidationStep } from './IdValidationStep';
import { ModuleSelectionStep } from './ModuleSelectionStep';
import { ConfigStep } from './ConfigStep';
import { PreflightStep } from './PreflightStep';
import { ConfirmStep } from './ConfirmStep';
import { useMassMigrationWizardStep } from '@/stores/massMigrationWizardStore';

const STEP_META: Record<number, { title: string; description: string }> = {
  0: {
    title: 'Organization Type',
    description: 'Choose the type of organizations you are migrating in this batch.',
  },
  1: {
    title: 'Upload CSV',
    description: 'Upload a CSV file containing all organizations to migrate.',
  },
  2: {
    title: 'ID Validation',
    description: 'Validate Bridge and Amplify IDs for each row before proceeding.',
  },
  3: {
    title: 'Module Selection',
    description: 'Choose which modules to migrate for each organization.',
  },
  4: {
    title: 'Configuration',
    description: 'Set per-module configuration overrides where needed.',
  },
  5: {
    title: 'Pre-flight Check',
    description: 'Run final validation across all selected modules before submitting.',
  },
  6: {
    title: 'Confirm & Submit',
    description: 'Review your batch configuration and start the mass migration.',
  },
};

function StepPlaceholder({ step }: { step: number }) {
  return (
    <div className="flex min-h-[200px] items-center justify-center rounded-md border border-dashed border-muted">
      <p className="text-sm text-muted-foreground">Step {step} — coming soon</p>
    </div>
  );
}

function renderStep(step: number) {
  switch (step) {
    case 0:
      return <OrgTypeStep />;
    case 1:
      return <UploadStep />;
    case 2:
      return <IdValidationStep />;
    case 3:
      return <ModuleSelectionStep />;
    case 4:
      return <ConfigStep />;
    case 5:
      return <PreflightStep />;
    case 6:
      return <ConfirmStep />;
    default:
      return <StepPlaceholder step={step} />;
  }
}

export function MassMigrationWizard() {
  const step = useMassMigrationWizardStep();
  const meta = STEP_META[step] ?? { title: `Step ${step}`, description: '' };

  return (
    <div className="container max-w-4xl py-8">
      <div className="mb-2">
        <h1 className="text-2xl font-semibold tracking-tight">Mass Migration</h1>
        <p className="text-sm text-muted-foreground">
          Migrate multiple organizations from a single CSV upload.
        </p>
      </div>

      <WizardProgress currentStep={step} />

      <Card>
        <CardHeader>
          <CardTitle>{meta.title}</CardTitle>
          <CardDescription>{meta.description}</CardDescription>
        </CardHeader>
        {/* key={step} forces full remount when navigating between steps, preventing stale local state */}
        <CardContent key={step}>
          {renderStep(step)}
        </CardContent>
      </Card>
    </div>
  );
}
